import React, { useCallback, useMemo, useState } from 'react';
import { GIFT_LEVEL_OPTIONS, PeriodTabType } from '@/constants/subscription';
import PricingCard, { CardTier } from '../pricing-card';
import type { BenefitSection } from '../benefit-list';
import styles from './index.module.css';
import {
  curSubProductAtom,
  curProProductAtom,
  activeIndexAtom,
  productsAtom,
  curPeriodTabAtom,
  giftLevelAtom,
  loadingAtom,
} from '@/store';
import { useAtom } from 'jotai';
import { getDisplayPrice } from '@/utils/vip';
import { Product } from '@/types/product';
import { handleRefreshQrCode } from '@/utils/subscribe';
import { getUrlParam } from '@/utils';
import { PromotionType } from '@/constants/subscription';
import PayQrModal from '../pay-qr-modal';
import ServiceNoticeModal from '@/components/service-notice-modal';
import { track } from '@/utils/common';
import { getLocaleStore } from '@/i18n/localeStore';

const { $t } = getLocaleStore();

/** 是否与 product-list 一致：首个优惠为低价试用 */
const isLowPriceTrialProduct = (product?: Product | null) => {
  const promotionInfo: any = product?.promotions?.[0];
  return promotionInfo?.promotionType === PromotionType.LowPriceTrialPromotion;
};

/** 获取商品展示价格文本 */
const formatPrice = (product?: Product) => {
  if (!product) return { price: '--', payPrice: undefined, moneySymbol: '¥' };
  const info = getDisplayPrice(product);
  return {
    price: info.price ?? '--',
    payPrice: info.payPrice,
    moneySymbol: info.moneySymbol || '¥',
  };
};

/** 月均金额展示（元），去掉多余小数尾 0 */
const formatMonthlyYuanText = (yuan: number) => {
  const n = Math.round(yuan * 100) / 100;
  if (!Number.isFinite(n) || n <= 0) return '';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2).replace(/\.?0+$/, '');
};

const parseDisplayYuan = (price: number | string | undefined): number | null => {
  if (price === undefined || price === '--' || price === '') return null;
  const n = typeof price === 'number' ? price : parseFloat(String(price));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * 连续包年 / 单年购买：主价格区展示月均价 = 年价展示价 ÷12，单位 /月。
 * 月均按 getDisplayPrice 的年费 price（含试用场景下的年费原价）折算，不用试用首期价。
 */
const getYearPackageMainPriceDisplay = (
  formatted: ReturnType<typeof formatPrice>
): { price: string; priceUnit: string } | null => {
  const yearlyYuan = parseDisplayYuan(formatted.price);
  if (yearlyYuan == null) return null;
  const text = formatMonthlyYuanText(yearlyYuan / 12);
  if (!text) return null;
  return {
    price: `${formatted.moneySymbol}${text}`,
    priceUnit: $t((v) => v.Text_PerMonth),
  };
};

/** 静态权益配置 */
const BENEFITS_FREE: BenefitSection[] = [
  {
    title: $t((v) => v.Title_FreeImageCreate),
    items: [
      { text: $t((v) => v.Text_AccountLimitOne) },
      { text: $t((v) => v.Text_PaidTemplateMaterial) },
      { text: $t((v) => v.Desc_PartialFreeSmartTools) },
    ],
  },
];

const BENEFITS_BASIC: BenefitSection[] = [
  {
    title: $t((v) => v.Title_IncludesFreePlus),
    items: [
      { text: $t((v) => v.Desc_Monthly330Meidou) },
      { text: $t((v) => v.Desc_AgentProductSuite), icon: 'tag' as const },
      { text: $t((v) => v.Desc_AiShootSuite), icon: 'tag' as const },
      { text: $t((v) => v.Desc_AiDesignSuite), icon: 'tag' as const },
      { text: $t((v) => v.Text_AccountLimitOne) },
      { text: $t((v) => v.Desc_PersonalCommercialAuth) },
      { text: $t((v) => v.Text_PaidTemplateMaterial) },
      { text: $t((v) => v.Desc_SmartToolsBundle) },
    ],
  },
];

/** 高级会员各档位美豆面板数据（来源：产品定价表） */
const PRO_MEIDOU_LEVEL_DATA: Record<
  number,
  {
    meidouText: string;
    unitPrice: string;
    resourceDesc: string;
    tagsMonthly: string[];
    tagsYearly: string[];
  }
> = {
  1: {
    meidouText: $t((v, { r }) => r(v.Text_MeidouPerMonth, [1000])),
    unitPrice: $t((v) => v.Text_UnitPrice1000),
    resourceDesc: $t((v) => v.Text_Capacity1000),
    tagsMonthly: [$t((v) => v.Text_SingleImageLow1000), $t((v) => v.Text_SingleVideoLow1000)],
    tagsYearly: [$t((v) => v.Text_SingleImageYearly1000), $t((v) => v.Text_SingleVideoYearly1000)],
  },
  2: {
    meidouText: $t((v, { r }) => r(v.Text_MeidouPerMonth, [1800])),
    unitPrice: $t((v) => v.Text_UnitPrice1800),
    resourceDesc: $t((v) => v.Text_Capacity1800),
    tagsMonthly: [$t((v) => v.Text_SingleImageLow1800), $t((v) => v.Text_SingleVideoLow1800)],
    tagsYearly: [$t((v) => v.Text_SingleImageYearly1800), $t((v) => v.Text_SingleVideoYearly1800)],
  },
  3: {
    meidouText: $t((v, { r }) => r(v.Text_MeidouPerMonth, [3600])),
    unitPrice: $t((v) => v.Text_UnitPrice3600),
    resourceDesc: $t((v) => v.Text_Capacity3600),
    tagsMonthly: [$t((v) => v.Text_SingleImageLow3600), $t((v) => v.Text_SingleVideoLow3600)],
    tagsYearly: [$t((v) => v.Text_SingleImageYearly3600), $t((v) => v.Text_SingleVideoYearly3600)],
  },
  4: {
    meidouText: $t((v, { r }) => r(v.Text_MeidouPerMonth, [7500])),
    unitPrice: $t((v) => v.Text_UnitPrice7500),
    resourceDesc: $t((v) => v.Text_Capacity7500),
    tagsMonthly: [$t((v) => v.Text_SingleImageLow7500), $t((v) => v.Text_SingleVideoLow7500)],
    tagsYearly: [$t((v) => v.Text_SingleImageYearly7500), $t((v) => v.Text_SingleVideoYearly7500)],
  },
};

/** 从档位文案解析每月美豆数，如「1000美豆/月」→ 1000 */
const getMonthlyMeidouCountForGiftLevel = (level: number): number => {
  const text = (PRO_MEIDOU_LEVEL_DATA[level] || PRO_MEIDOU_LEVEL_DATA[1]).meidouText;
  const m = text.match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : 1000;
};

const buildBenefitsPro = (monthlyMeidou: number): BenefitSection[] => [
  {
    title: $t((v) => v.Desc_IncludesStandardAll),
    items: [
      { text: $t((v, { r }) => r(v.Desc_MonthlyMeidou, [monthlyMeidou])) },
      { text: $t((v) => v.Desc_AgentProductSuite), icon: 'tag' as const },
      { text: $t((v) => v.Desc_AiShootSuite), icon: 'tag' as const },
      { text: $t((v) => v.Desc_AiShootSuite), icon: 'tag' as const },
    ],
  },
];

const SubProducts: React.FC = () => {
  const [curSubProduct] = useAtom(curSubProductAtom);
  const [curProProduct] = useAtom(curProProductAtom);
  const [, setActiveIndex] = useAtom(activeIndexAtom);
  const [, setProducts] = useAtom(productsAtom);
  const [curPeriodTab] = useAtom(curPeriodTabAtom);
  const [loading] = useAtom(loadingAtom);
  const [visiblePayQr, setVisiblePayQr] = useState(false);
  const [serviceNoticeVisible, setServiceNoticeVisible] = useState(false);
  const [giftLevel] = useAtom(giftLevelAtom);
  const hideLowPriceTrialCopywriting = +(getUrlParam('hideLowPriceTrialCopywriting') || '');

  const handleSubscribe = useCallback(
    (tier: CardTier) => {
      const product = tier === 'basic' ? curSubProduct : curProProduct;
      if (!product) return;

      // 将选中的商品写入 productsAtom 以供 curProductAtom 派生
      setProducts([product as Product]);
      setActiveIndex(0);
      handleRefreshQrCode();
      setVisiblePayQr(true);

      track({
        event_id: 'vip_page_click',
        event_name: '会员落地页点击',
        params: { class: 'pay_now', name_id: product.productId || '' },
      });
    },
    [curSubProduct, curProProduct, setProducts, setActiveIndex]
  );

  const subPrice = useMemo(() => formatPrice(curSubProduct as Product), [curSubProduct]);
  const proPrice = useMemo(() => formatPrice(curProProduct as Product), [curProProduct]);

  const subLowPriceTrial =
    isLowPriceTrialProduct(curSubProduct as Product) && !hideLowPriceTrialCopywriting;
  const proLowPriceTrial =
    isLowPriceTrialProduct(curProProduct as Product) && !hideLowPriceTrialCopywriting;

  const CARD_CONFIGS = useMemo(() => {
    /** 仅首屏拉品阶段（尚无标准/高级商品）：三张骨架；支付轮询 loading 不触发 */
    const showThreeCardSkeletonRow = loading && !curSubProduct && !curProProduct;

    const showYearPriceAsMonthlyAvg =
      curPeriodTab === PeriodTabType.YEARLY || curPeriodTab === PeriodTabType.SINGLE_YEARLY;
    const subYearlyMain = showYearPriceAsMonthlyAvg
      ? getYearPackageMainPriceDisplay(subPrice)
      : null;
    const proYearlyMain = showYearPriceAsMonthlyAvg
      ? getYearPackageMainPriceDisplay(proPrice)
      : null;

    const proMonthlyMeidouCount = getMonthlyMeidouCountForGiftLevel(giftLevel);

    return [
      {
        tier: 'free' as CardTier,
        title: $t((v) => v.Text_FreeEdition),
        skeleton: showThreeCardSkeletonRow,
        price: '¥0',
        priceUnit: $t((v) => v.Text_PerMonth),
        buttonText: $t((v) => v.Title_Button3_Pricing),
        buttonDisabled: true,
        highlighted: false,
        meidouPanel: { meidouText: $t((v) => v.Text_FewCredits) },
        benefits: BENEFITS_FREE,
      },
      {
        tier: 'basic' as CardTier,
        title: $t((v) => v.Label_BasicPlan),
        skeleton: showThreeCardSkeletonRow,
        price: (() => {
          // 低价试用：包年/单年 Tab 也直接展示支付价，不做年价÷12
          if (showYearPriceAsMonthlyAvg && subLowPriceTrial) {
            return `${subPrice.moneySymbol}${
              subPrice.payPrice != null ? subPrice.payPrice : subPrice.price
            }`;
          }
          if (subYearlyMain) return subYearlyMain.price;
          return `${subPrice.moneySymbol}${
            subLowPriceTrial && subPrice.payPrice != null ? subPrice.payPrice : subPrice.price
          }`;
        })(),
        priceUnit: $t((v) => v.Text_PerMonth),
        priceDesc: subLowPriceTrial ? $t((v) => v.Text_Trial7Days) : undefined,
        productDesc: curSubProduct?.productDesc || '',
        explain: (curSubProduct as Product)?.checkBox?.explain || '',
        buttonText: $t((v) => v.Button_SubscribeNow),
        buttonDisabled: !curSubProduct,
        highlighted: false,
        badge: curSubProduct?.promotionBanner,
        meidouPanel: {
          meidouText: subLowPriceTrial
            ? $t((v) => v.Text_TrialMeidouGift)
            : $t((v) => v.Text_Monthly330MeidouBenefit),
        },
        benefits: BENEFITS_BASIC,
      },
      {
        tier: 'pro' as CardTier,
        title: $t((v) => v.Text_PremiumEdition),
        skeleton: showThreeCardSkeletonRow,
        price: (() => {
          if (showYearPriceAsMonthlyAvg && proLowPriceTrial) {
            return `${proPrice.moneySymbol}${
              proPrice.payPrice != null ? proPrice.payPrice : proPrice.price
            }`;
          }
          if (proYearlyMain) return proYearlyMain.price;
          return `${proPrice.moneySymbol}${
            proLowPriceTrial && proPrice.payPrice != null ? proPrice.payPrice : proPrice.price
          }`;
        })(),
        priceUnit: $t((v) => v.Text_PerMonth),
        priceDesc: proLowPriceTrial ? $t((v) => v.Text_Trial7Days) : undefined,
        productDesc: curProProduct?.productDesc || '',
        explain: (curProProduct as Product)?.checkBox?.explain || '',
        buttonText: $t((v) => v.Button_SubscribeNow),
        buttonDisabled: !curProProduct,
        highlighted: true,
        badge: curProProduct?.promotionBanner,
        meidouPanel: (() => {
          const levelData = PRO_MEIDOU_LEVEL_DATA[giftLevel] || PRO_MEIDOU_LEVEL_DATA[1];
          const isYearly =
            curPeriodTab === PeriodTabType.YEARLY || curPeriodTab === PeriodTabType.SINGLE_YEARLY;
          return {
            meidouText: levelData.meidouText,
            unitPrice: levelData.unitPrice,
            resourceDesc: levelData.resourceDesc,
            tags: isYearly ? levelData.tagsYearly : levelData.tagsMonthly,
            expandable: true,
            levelOptions: GIFT_LEVEL_OPTIONS,
          };
        })(),
        benefits: buildBenefitsPro(proMonthlyMeidouCount),
        specialBenefits: {
          title: $t((v) => v.Label_LimitedPrivilege),
          items: [{ text: $t((v) => v.Text_SeedancePrivilege) }],
        },
      },
    ].filter((c) => {
      // 首屏拉品：保留三张骨架；有商品数据后按是否存在标准/高级商品过滤
      if (showThreeCardSkeletonRow) return true;
      if (c.tier === 'free') return true;
      if (c.tier === 'basic') return !!curSubProduct;
      if (c.tier === 'pro') return !!curProProduct;
      return true;
    });
  }, [
    subPrice,
    proPrice,
    curSubProduct,
    curProProduct,
    subLowPriceTrial,
    proLowPriceTrial,
    giftLevel,
    curPeriodTab,
    loading,
  ]);

  return (
    <>
      <div className={styles.cardsContainer}>
        {CARD_CONFIGS.map((config) => (
          <PricingCard
            key={config.tier}
            {...config}
            onButtonClick={config.buttonDisabled ? undefined : () => handleSubscribe(config.tier)}
            onExplainIconClick={config.explain ? () => setServiceNoticeVisible(true) : undefined}
          />
        ))}
      </div>
      <ServiceNoticeModal
        visible={serviceNoticeVisible}
        onClose={() => setServiceNoticeVisible(false)}
        variant="renewal"
      />
      <PayQrModal visible={visiblePayQr} onClose={() => setVisiblePayQr(false)} />
    </>
  );
};

export default SubProducts;
